import { ref, onMounted } from 'vue';
import type { Ref } from 'vue';
import { debouncedExponentialRetry } from './utils/debounced-retry';
import { isRetryError } from './campaign/utils/validation';
import logger from './logger';
import { useAuthController, useDPOAuth } from './auth';
import { useApis } from './apis';
import { ResponseError } from './apis/generated/dialpad-contact';
import type {
  User,
  OperatorGroup,
  DialpadContactApi,
} from './apis/generated/dialpad-contact';
import type { AuthController, DPOAuth } from '@dialpad/auth';

export type PDUser = Omit<
  User,
  'operatorGroups' | 'experiments' | 'adminGroups'
> & {
  operatorGroups: Map<number, OperatorGroup>;
  experiments: Set<string>;
  adminGroups: Set<string>;
  adminOffices: Set<string>;
};

type UseUserData = {
  me: Ref<PDUser | null>;
  loading: Ref<boolean>;
  error: Ref<Error | null>;
};

const me = ref<PDUser | null>(null);
const loading = ref(true);
const error = ref<Error | null>(null);
let hasFetched = false;

const log = logger.getChild('use-user');

enum ROLES {
  ADMIN = 'role:admin',
  OPERATOR = 'role:operator',
}

const mapUser = (user: User, officesIds: string[]): PDUser => {
  const operatorGroups = new Map<number, OperatorGroup>();
  for (const group of user.operatorGroups ?? []) {
    operatorGroups.set(group.groupId, group);
  }
  const adminOffices = new Set<string>();
  for (const officeId of officesIds) {
    if (officeId !== user.officeKey) {
      adminOffices.add(officeId);
      continue;
    }
    if (user.isAdmin) {
      // current office returned for non-admin users too
      adminOffices.add(officeId);
    }
  }
  return {
    ...user,
    operatorGroups,
    experiments: new Set(user.experiments),
    adminGroups: new Set(
      user.groupDetails
        ?.filter((g) => g.access.includes(ROLES.ADMIN))
        .map((g) => g.id) ?? [],
    ),
    adminOffices,
  };
};

const PROJECTION = [
  'id',
  'key',
  'short_key',
  'company',
  'first_name',
  'last_name',
  'display_name',
  'image_url',
  'is_admin',
  'is_super_admin',
  'is_google_admin',
  'is_microsoft_admin',
  'ivr_language',
  'locale',
  'operator_groups',
  'experiments',
  'admin_groups',
  'office_key',
  'group_details',
].join(',');

type FetchArgs = {
  authController: AuthController;
  contactApi: DialpadContactApi;
  dpOAuth: DPOAuth;
};
const { debounced: fetchUser } = debouncedExponentialRetry(
  // eslint-disable-next-line max-statements
  async ({
    authController,
    contactApi,
    dpOAuth,
  }: FetchArgs): Promise<PDUser | null> => {
    try {
      log.debug('Fetching user');
      loading.value = true;

      await authController.waitForAuthorized();

      // do 2 requests in parallel
      const officesPromise = contactApi.getMyOffices({
        includeRbac: true,
        keysOnly: true,
        mine: true,
        shortKey: true,
      });
      const user = await contactApi.getUserById({
        userId: 'me',
        projection: PROJECTION,
        shortKey: true,
      });
      const offices = await officesPromise;

      me.value = mapUser(user, offices);

      return me.value;
    } catch (catchError: unknown) {
      if (isRetryError(catchError)) {
        log.log('Failed to fetch user, retrying', catchError);
        throw catchError;
      }

      if (
        catchError instanceof ResponseError &&
        catchError.response.status === 401
      ) {
        log.log('Unauthorized, logging out');
        const authData = await authController.signOut();
        dpOAuth.redirectToSignout(authData);
        return null;
      }
      log.log('Non-recoverable error fetching user', catchError);
      error.value = catchError as Error;
      return null;
    } finally {
      loading.value = false;
      hasFetched = true;
    }
  },
  // some minimal delay to prevent multiple requests
  50,
);

export { fetchUser };

export const useUser = (): UseUserData => {
  onMounted(() => {
    if (!hasFetched) {
      const authController = useAuthController();
      const { contactApi } = useApis();
      const dpOAuth = useDPOAuth();
      void fetchUser({
        authController,
        contactApi,
        dpOAuth,
      });
    }
  });

  return {
    me,
    loading,
    error,
  };
};
